// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { DelayedFeedbackCopy } from '@tectonics/components';
import { IfFeature } from '@tectonics/prs/feature';
import {
  usePendingIncomingPayment,
  getSafeClabeAccount,
  getSafeClabeReference,
  getSafeSpeiUrl,
} from '@tectonics/prs/incoming-payment';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import copyImgSrc from './copy.svg';
import { StatusMessage } from '../../status-message';
import {
  VoucherTitle,
  VoucherMailWarning,
  VoucherTicket,
  VoucherTicketAmount,
  VoucherTicketDueDate,
  VoucherListing,
  VoucherListingWrapper,
  VoucherListingItem,
  VoucherListingItemTitle,
  VoucherListingItemValue,
  VoucherTips,
  VoucherTipsTitle,
  VoucherTipsItems,
  VoucherTipItem,
} from '../commons/voucher';
import { Separator, Button } from '../../../../atoms';

export function PageContent(): Node {
  const allFeatures = useAllFeatures();
  const incomingPayment = usePendingIncomingPayment();

  const clabeAccount = getSafeClabeAccount(incomingPayment);
  const clabelReference = getSafeClabeReference(incomingPayment);

  return (
    <div>
      <VoucherTitle>Falta poco para finalizar tu compra.</VoucherTitle>

      <IfFeature allFeatures={allFeatures} wanted="display_spei_mail_warning">
        <VoucherMailWarning />
      </IfFeature>

      <Separator direction="vertical" size={4} />

      <VoucherTicket>
        <IfFeature allFeatures={allFeatures} wanted="display_spei_total_amount">
          <VoucherTicketAmount />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_spei_due_date">
          <VoucherTicketDueDate />
        </IfFeature>
      </VoucherTicket>

      <Separator direction="vertical" size={4.5} />

      <VoucherListing>
        <VoucherListingWrapper>
          <IfFeature allFeatures={allFeatures} wanted="display_spei_destination_clabe_number">
            <VoucherListingItem>
              <VoucherListingItemTitle>CLABE interbancaria</VoucherListingItemTitle>
              <VoucherListingItemValue>
                <DelayedFeedbackCopy as={CopyContainer} delay={1500} value={clabeAccount}>
                  <span>{clabeAccount}</span>
                  <CopyImage src={copyImgSrc} alt="copy paste icon" />
                </DelayedFeedbackCopy>
              </VoucherListingItemValue>
            </VoucherListingItem>
          </IfFeature>

          <IfFeature allFeatures={allFeatures} wanted="display_spei_reference_number">
            <VoucherListingItem>
              <VoucherListingItemTitle>Número de referencia</VoucherListingItemTitle>
              <VoucherListingItemValue>
                <DelayedFeedbackCopy as={CopyContainer} delay={1500} value={clabelReference}>
                  <span>{clabelReference}</span>
                  <CopyImage src={copyImgSrc} alt="copy paste icon" />
                </DelayedFeedbackCopy>
              </VoucherListingItemValue>
            </VoucherListingItem>
          </IfFeature>

          <IfFeature allFeatures={allFeatures} wanted="display_spei_destination_bank">
            <VoucherListingItem>
              <VoucherListingItemTitle>Banco destino</VoucherListingItemTitle>
              <VoucherListingItemValue>
                Sistema de transferencia de pagos (STP)
              </VoucherListingItemValue>
            </VoucherListingItem>
          </IfFeature>

          <IfFeature allFeatures={allFeatures} wanted="display_spei_destination_beneficiary">
            <VoucherListingItem>
              <VoucherListingItemTitle>Beneficiario</VoucherListingItemTitle>
              <VoucherListingItemValue>EBANX</VoucherListingItemValue>
            </VoucherListingItem>
          </IfFeature>
        </VoucherListingWrapper>
      </VoucherListing>

      <Separator pattern="dotted" direction="vertical" size={5.5} />

      <IfFeature allFeatures={allFeatures} wanted="display_spei_tips">
        <VoucherTips>
          <VoucherTipsTitle>
            Consejos de pago!
          </VoucherTipsTitle>

          <VoucherTipsItems>
            <VoucherTipItem>
              Ingresa a tu banca en línea y registra la CLABE interbancaria que aparece en la boleta. Puede demorar hasta 24 horas para que la CLABE quede registrada.
            </VoucherTipItem>

            <VoucherTipItem>
              Para garantizar tu pago, digita el número de referencia y el total a pagar tal y como aparece en tu boleta.
            </VoucherTipItem>

            <VoucherTipItem>
              Identifica como banco destino a Sistema de transferencia de pagos (STP) y a EBANX como beneficiario.
            </VoucherTipItem>

            <VoucherTipItem>
              Confirma los datos y realiza la transferencia.
            </VoucherTipItem>
          </VoucherTipsItems>
        </VoucherTips>
      </IfFeature>

      <Separator direction="vertical" size={4} />

      <IfFeature allFeatures={allFeatures} wanted="action_spei_save_pdf">
        <LinkButton type="primary" block as="a" target="_blank" href={`${getSafeSpeiUrl(incomingPayment)}&format=pdf`}>
          Guardar instrucciones en PDF
        </LinkButton>
        <Separator direction="vertical" size={4}/>
      </IfFeature>

      <StatusMessage />
    </div>
  );
}

const CopyContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const CopyImage = styled.img`
  height: 16px;
  width: auto;
  margin-left: 4px;
`;

const LinkButton = styled(Button)`
  padding: 16px 8px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;
